// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Image, Modal, Platform, Pressable, ScrollView, StyleSheet, Switch, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { useIsFocused } from '@react-navigation/native';
import { usePathname, useRouter } from 'expo-router';
import { SvgXml } from 'react-native-svg';
import * as WebBrowser from 'expo-web-browser';
import { Text } from '@/components/Themed';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import DeleteAccountModal from '@/components/DeleteAccountModal';
import GoffyButton from '@/components/GoffyButton';
import GoffyInput from '@/components/GoffyInput';
import PlatformDatePicker from '@/components/PlatformDatePicker';
import { CoachMarkCallout } from '@/components/CoachMarkCallout';
import { buildLogoutUrl, POST_LOGOUT_REDIRECT, revokeRefreshToken } from '@/auth/oidc';
import { useUser } from '@/app/user-context';
import { useChangePassword } from '@/hooks/useChangePassword';
import { useDeleteAccount } from '@/hooks/useDeleteAccount';
import { useI18n } from '@/i18n';
import { parseDistanceUnit, type DistanceUnit } from '@/features/units/distance';
import { useDistanceUnit, useDistanceUnitState } from '@/stores/distanceUnitState';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useShotTrackingState } from '@/stores/shotTrackingState';
import { useShowDistanceState } from '@/stores/showDistanceState';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { createAvatarSvg } from '@/services/avatarSvg';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import {
  profileImageActionsCoachMarkKey,
  useCoachMark,
  useCoachMarkPreferences,
} from '@/services/coachMarks';
import { ensureCurrentPlayer } from '@/services/ensureCurrentPlayer';
import { formatLocalDate, localDateToDate } from '@/services/localDate';
import appColors from '@/theme/colors';
import { parseDecimalInput } from '@/utils/decimalInput';

type PlayerProfile = {
  id: string;
  name?: string;
  displayName?: string | null;
  imageKey?: string | null;
  imageThumbKey?: string | null;
  gender?: string | null;
  handicap?: number | null;
  handicapDate?: string | null;
  Gender?: string | null;
  Handicap?: number | null;
  HandicapDate?: string | null;
};

type UserProfile = {
  id: string;
  username?: string;
  displayName?: string | null;
  fullName?: string | null;
  email?: string | null;
  createdAt?: string;
  golfId?: string | null;
  searchVisibility?: string | null;
  enhancedInput?: boolean | null;
  distanceUnit?: string | null;
  homeClubs?: HomeClubProfile[] | null;
};

/** Who may discover this user: by name, by an exact Golf ID only, or nobody. */
type SearchVisibility = 'name' | 'golfId' | 'never';

const searchVisibilityOptions: SearchVisibility[] = ['name', 'golfId', 'never'];

const searchVisibilityLabelKeys: Record<SearchVisibility, string> = {
  name: 'profile.searchVisibilityName',
  golfId: 'profile.searchVisibilityGolfId',
  never: 'profile.searchVisibilityNever',
};

const searchVisibilityHelpKeys: Record<SearchVisibility, string> = {
  name: 'profile.searchVisibilityNameHelp',
  golfId: 'profile.searchVisibilityGolfIdHelp',
  never: 'profile.searchVisibilityNeverHelp',
};

function parseSearchVisibility(value: string | null | undefined): SearchVisibility {
  return searchVisibilityOptions.includes(value as SearchVisibility)
    ? (value as SearchVisibility)
    : 'name';
}

const golfIdMaxLength = 32;

/**
 * Mirrors the loose server-side rule: any national federation format is fine, but the value has to
 * look like an identifier rather than free text.
 */
function isValidGolfId(value: string): boolean {
  if (value.length === 0) {
    return true;
  }
  if (value.length > golfIdMaxLength) {
    return false;
  }
  if (!/[\p{L}\p{N}]/u.test(value)) {
    return false;
  }
  return /^[\p{L}\p{N}\- ]+$/u.test(value);
}

type HomeClubProfile = {
  clubId: string;
  clubName: string;
  clubCity?: string | null;
  isPrimary?: boolean;
};

type ClubOption = {
  id: string;
  name: string;
  city?: string;
  country?: string;
};

type PendingUpload = {
  blob: Blob;
  contentType: string;
  fileName: string;
  fileSize: number;
};

export default function ProfileScreen() {
  const { user, setUser } = useUser();
  const { t } = useI18n();
  const isFocused = useIsFocused();
  const pathname = usePathname();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const imageActionsAnchorRef = useRef<View>(null);
  const [imageCoachMarkDismissed, setImageCoachMarkDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [player, setPlayer] = useState<PlayerProfile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [initialEmail, setInitialEmail] = useState('');
  const [golfId, setGolfId] = useState('');
  const [initialGolfId, setInitialGolfId] = useState('');
  const [searchVisibility, setSearchVisibility] = useState<SearchVisibility>('name');
  const [initialSearchVisibility, setInitialSearchVisibility] = useState<SearchVisibility>('name');
  const [enhancedInput, setEnhancedInput] = useState(false);
  const [enhancedInputSaving, setEnhancedInputSaving] = useState(false);
  // Held in the shared store rather than local state: every screen that draws a distance
  // reads the same value, so it has to change everywhere the moment it is picked.
  const distanceUnit = useDistanceUnit();
  const shotTrackingEnabled = useShotTrackingState((state) => state.enabled);
  const setShotTrackingEnabled = useShotTrackingState((state) => state.setEnabled);
  const showDistanceEnabled = useShowDistanceState((state) => state.enabled);
  const setShowDistanceEnabled = useShowDistanceState((state) => state.setEnabled);
  const { isDeveloper } = useEffectiveRoles(user);
  const setDistanceUnit = useDistanceUnitState((state) => state.setUnit);
  const [distanceUnitSaving, setDistanceUnitSaving] = useState(false);
  const [homeClubs, setHomeClubs] = useState<HomeClubProfile[]>([]);
  const [initialHomeClubs, setInitialHomeClubs] = useState<HomeClubProfile[]>([]);
  const [clubOptions, setClubOptions] = useState<ClubOption[]>([]);
  const [loadingClubOptions, setLoadingClubOptions] = useState(false);
  const [clubPickerOpen, setClubPickerOpen] = useState(false);
  const [clubSearch, setClubSearch] = useState('');
  const [gender, setGender] = useState('');
  const [handicap, setHandicap] = useState('');
  const [handicapDate, setHandicapDate] = useState('');
  const [handicapDatePickerOpen, setHandicapDatePickerOpen] = useState(false);
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [avatarChoices, setAvatarChoices] = useState<Array<{ id: string; seed: string }>>([]);
  const [avatarRemovedLocally, setAvatarRemovedLocally] = useState(false);
  const avatarRefreshLock = useRef(false);
  const pendingUploadRef = useRef<PendingUpload | null>(null);
  const imageRequestIdRef = useRef<string | null>(null);
  const updateRequestIdRef = useRef<string | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  // The coach mark renders its own full-screen Modal; react-native-web aria-hides
  // sibling modals beneath the most recently mounted one, so keep it off while any
  // dialog on this screen is open.
  const anyDialogOpen =
    passwordModalOpen ||
    deleteAccountModalOpen ||
    avatarPickerOpen ||
    clubPickerOpen ||
    handicapDatePickerOpen;

  const clearSaveTimeout = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  }, []);

  /**
   * A profile update only fails via a player.update.error reply or this timeout — never via the
   * send() return value: 'queued' means the frame is flushed when the socket (re)opens and the
   * update is still in flight.
   */
  const armSaveTimeout = useCallback(
    (requestId: string) => {
      clearSaveTimeout();
      saveTimeoutRef.current = setTimeout(() => {
        saveTimeoutRef.current = null;
        if (updateRequestIdRef.current !== requestId) {
          return;
        }
        updateRequestIdRef.current = null;
        setSaving(false);
        setStatusNote(null);
        setError(t('profile.saveFailed'));
      }, 12000);
    },
    [clearSaveTimeout, t]
  );

  useEffect(() => clearSaveTimeout, [clearSaveTimeout]);
  const {
    visible: showImageCoachMark,
    dismissForever: dismissImageCoachMarkForever,
    disableAll: disableAllCoachMarks,
  } = useCoachMark(
    apiBaseUrl,
    profileImageActionsCoachMarkKey,
    !imageCoachMarkDismissed && isFocused && !anyDialogOpen
  );

  const {
    enabled: coachTipsEnabled,
    loading: coachTipsLoading,
    saving: coachTipsSaving,
    hasDismissedTips,
    setEnabled: setCoachTipsEnabled,
    resetAll: resetCoachTips,
  } = useCoachMarkPreferences(apiBaseUrl);
  const [coachTipsError, setCoachTipsError] = useState<string | null>(null);

  const {
    changePassword,
    loading: passwordSaving,
    error: passwordError,
    reset: resetPasswordState,
  } = useChangePassword({
    apiBaseUrl,
    token: user?.token,
    onSuccess: () => {
      setPasswordModalOpen(false);
      setHasPassword(true);
      setStatusNote(t('profile.passwordChanged'));
    },
  });

  /**
   * The account is already gone server-side by the time this runs, so every step is best-effort:
   * clear the local session first, then leave the screen, then try to tidy up the Keycloak side.
   */
  const finishAccountDeletion = useCallback(async () => {
    const idTokenHint = user?.idToken ?? user?.token ?? null;
    const refreshToken = user?.refreshToken ?? null;
    setDeleteAccountModalOpen(false);
    setUser(null);
    router.replace('/');
    if (refreshToken) {
      try {
        await revokeRefreshToken(refreshToken);
      } catch {
        // Deleting the Keycloak user already invalidated the token; nothing to recover here.
      }
    }
    const logoutUrl = buildLogoutUrl(idTokenHint);
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
        window.location.href = logoutUrl;
      } else {
        await WebBrowser.openAuthSessionAsync(logoutUrl, POST_LOGOUT_REDIRECT);
      }
    } catch {
      // The local session is cleared either way, so a failed SSO round trip is not worth surfacing.
    }
  }, [router, setUser, user?.idToken, user?.refreshToken, user?.token]);

  const {
    deleteAccount,
    loading: deletingAccount,
    error: deleteAccountError,
    reset: resetDeleteAccountState,
  } = useDeleteAccount({
    apiBaseUrl,
    userId: user?.id,
    token: user?.token,
    onDeleted: finishAccountDeletion,
  });

  const resolvedImage = avatarRemovedLocally ? null : player?.imageThumbKey || player?.imageKey || null;
  const genderOptions = useMemo(
    () => [
      { value: 'male', label: t('profile.genderMale') },
      { value: 'female', label: t('profile.genderFemale') },
      { value: 'neutral', label: t('profile.genderNeutral') },
    ],
    [t]
  );
  const normalizedHomeClubs = useCallback(
    (items: HomeClubProfile[]) =>
      items
        .map((item, index) => ({
          clubId: item.clubId,
          clubName: item.clubName?.trim() ?? '',
          clubCity: item.clubCity?.trim() || null,
          isPrimary: item.isPrimary === true,
          _order: index,
        }))
        .sort((a, b) => {
          if (a.isPrimary === b.isPrimary) {
            if (a.clubId === b.clubId) {
              return a._order - b._order;
            }
            return a.clubId.localeCompare(b.clubId);
          }
          return a.isPrimary ? -1 : 1;
        })
        .map(({ _order, ...rest }) => rest),
    []
  );
  const filteredClubOptions = useMemo(() => {
    const query = clubSearch.trim().toLowerCase();
    const selectedIds = new Set(homeClubs.map((club) => club.clubId));
    const available = clubOptions.filter((club) => !selectedIds.has(club.id));
    if (!query) {
      return available;
    }
    return available.filter((club) => {
      const name = club.name.toLowerCase();
      const city = (club.city ?? '').toLowerCase();
      const country = (club.country ?? '').toLowerCase();
      return name.includes(query) || city.includes(query) || country.includes(query);
    });
  }, [clubOptions, clubSearch, homeClubs]);

  const resetUserFields = useCallback(() => {
    setFullName('');
    setEmail('');
    setInitialEmail('');
    setGolfId('');
    setInitialGolfId('');
    setSearchVisibility('name');
    setInitialSearchVisibility('name');
    setEnhancedInput(false);
    setHomeClubs([]);
    setInitialHomeClubs([]);
  }, []);

  const loadPlayer = useCallback(async () => {
    if (!user?.token || !user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const ensured = await ensureCurrentPlayer(apiBaseUrl, user.token, user.id, {
        timeoutMs: 12_000,
        intervalMs: 500,
      });

      if (ensured.status === 'timeout' || ensured.status === 'notFound') {
        setStatusNote(t('profile.playerProvisioning'));
        setPlayer(null);
        return;
      }

      if (ensured.status === 'unauthorized') {
        throw new Error('profile.load.failed.unauthorized');
      }

      if (ensured.status === 'error') {
        throw new Error(ensured.message);
      }

      const response = await fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (response.status === 404) {
        setStatusNote(t('profile.playerProvisioning'));
        setPlayer(null);
        return;
      }
      if (!response.ok) {
        throw new Error('profile.load.failed');
      }
      const payload = (await response.json()) as PlayerProfile;
      setStatusNote(null);
      setPlayer(payload);
      setDisplayName(payload.displayName ?? payload.name ?? '');
      try {
        const userResponse = await fetch(`${apiBaseUrl}/api/users/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (userResponse.ok) {
          const userPayload = (await userResponse.json()) as UserProfile;
          const loadedFullName = (userPayload.fullName ?? userPayload.displayName ?? '').trim();
          setFullName(loadedFullName);
          const loadedEmail = (userPayload.email ?? '').trim();
          setEmail(loadedEmail);
          setInitialEmail(loadedEmail);
          const loadedGolfId = (userPayload.golfId ?? '').trim();
          setGolfId(loadedGolfId);
          setInitialGolfId(loadedGolfId);
          const loadedVisibility = parseSearchVisibility(userPayload.searchVisibility);
          setSearchVisibility(loadedVisibility);
          setInitialSearchVisibility(loadedVisibility);
          setEnhancedInput(userPayload.enhancedInput === true);
          // The profile is the source of truth; this overwrites the locally cached mirror.
          setDistanceUnit(parseDistanceUnit(userPayload.distanceUnit));
          const loadedHomeClubs = (userPayload.homeClubs ?? []).map((club) => ({
            clubId: club.clubId,
            clubName: club.clubName,
            clubCity: club.clubCity ?? null,
            isPrimary: club.isPrimary === true,
          }));
          const normalizedLoadedHomeClubs = normalizedHomeClubs(loadedHomeClubs);
          setHomeClubs(normalizedLoadedHomeClubs);
          setInitialHomeClubs(normalizedLoadedHomeClubs);
        } else {
          resetUserFields();
        }
      } catch {
        resetUserFields();
      }
      try {
        const passwordResponse = await fetch(`${apiBaseUrl}/api/users/${user.id}/password`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        if (passwordResponse.ok) {
          const passwordPayload = (await passwordResponse.json()) as { hasPassword?: boolean };
          setHasPassword(passwordPayload.hasPassword === true);
        } else {
          setHasPassword(null);
        }
      } catch {
        setHasPassword(null);
      }
      const nextGender = payload.gender ?? payload.Gender ?? '';
      const nextHandicap = payload.handicap ?? payload.Handicap;
      const nextHandicapDate = payload.handicapDate ?? payload.HandicapDate ?? '';
      setGender(nextGender);
      setHandicap(nextHandicap === null || nextHandicap === undefined ? '' : String(nextHandicap));
      setHandicapDate(nextHandicapDate ? String(nextHandicapDate).slice(0, 10) : '');
    } catch {
      setError(t('profile.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, normalizedHomeClubs, resetUserFields, setDistanceUnit, t, user?.id, user?.token]);

  useEffect(() => {
    loadPlayer();
  }, [loadPlayer]);

  const loadClubOptions = useCallback(async () => {
    if (!user?.token) return;
    setLoadingClubOptions(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/geodata/clubs`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      if (!response.ok) {
        throw new Error('profile.homeClubsLoadFailed');
      }

      const payload = (await response.json()) as ClubOption[];
      setClubOptions(payload ?? []);
    } catch {
      setError(t('profile.homeClubsLoadFailed'));
    } finally {
      setLoadingClubOptions(false);
    }
  }, [apiBaseUrl, t, user?.token]);

  useEffect(() => {
    void loadClubOptions();
  }, [loadClubOptions]);

  useEffect(() => {
    const socket = socketRef.current;
    socket.setAuthToken(user?.token ?? null);
    const unsubscribe = socket.onMessage((message) => {
      const type = (message as { type?: string }).type;
      if (type === 'player.image.presign.ack') {
        const payload = message as { requestId?: string; uploadUrl?: string; publicUrl?: string };
        if (imageRequestIdRef.current && payload.requestId !== imageRequestIdRef.current) {
          return;
        }
        const pending = pendingUploadRef.current;
        if (!pending || !payload.uploadUrl || !payload.publicUrl) {
          imageRequestIdRef.current = null;
          pendingUploadRef.current = null;
          setUploading(false);
          setError(t('profile.uploadFailed'));
          return;
        }
        (async () => {
          try {
            const uploadResp = await fetch(payload.uploadUrl!, {
              method: 'PUT',
              headers: {
                'Content-Type': pending.contentType,
                'Cache-Control': 'public, max-age=31536000, immutable',
              },
              body: pending.blob,
            });
            if (!uploadResp.ok) {
              throw new Error(`Upload failed ${uploadResp.status}`);
            }
            const updateRequestId = generateId();
            updateRequestIdRef.current = updateRequestId;
            socketRef.current.send({
              type: 'player.profile.update',
              playerId: player?.id,
              requestId: updateRequestId,
              imageKey: payload.publicUrl,
              removeAvatar: false,
            });
            setAvatarRemovedLocally(false);
            setPlayer((prev) => (prev ? { ...prev, imageKey: payload.publicUrl, imageThumbKey: payload.publicUrl } : prev));
            setStatusNote(t('profile.uploadSuccess'));
          } catch {
            setError(t('profile.uploadFailed'));
          } finally {
            setUploading(false);
            imageRequestIdRef.current = null;
            pendingUploadRef.current = null;
          }
        })();
        return;
      }
      if (type === 'player.image.presign.error') {
        const payload = message as { requestId?: string; message?: string };
        if (imageRequestIdRef.current && payload.requestId !== imageRequestIdRef.current) {
          return;
        }
        imageRequestIdRef.current = null;
        pendingUploadRef.current = null;
        setUploading(false);
        setError(payload.message ?? t('profile.uploadFailed'));
        return;
      }
      if (type === 'player.update.ack') {
        const payload = message as { requestId?: string };
        console.info('[profile] player.update.ack received', {
          expectedRequestId: updateRequestIdRef.current,
          receivedRequestId: payload.requestId,
        });
        if (updateRequestIdRef.current && payload.requestId !== updateRequestIdRef.current) {
          console.info('[profile] player.update.ack ignored due to requestId mismatch');
          return;
        }
        updateRequestIdRef.current = null;
        clearSaveTimeout();
        setInitialEmail(email.trim());
        setInitialGolfId(golfId.trim());
        setInitialSearchVisibility(searchVisibility);
        setInitialHomeClubs(normalizedHomeClubs(homeClubs));
        setSaving(false);
        setStatusNote(t('profile.saveSuccess'));
        return;
      }
      if (type === 'player.update.error') {
        const payload = message as { requestId?: string; message?: string; code?: string };
        console.warn('[profile] player.update.error received', {
          expectedRequestId: updateRequestIdRef.current,
          receivedRequestId: payload.requestId,
          message: payload.message,
        });
        if (updateRequestIdRef.current && payload.requestId !== updateRequestIdRef.current) {
          console.warn('[profile] player.update.error ignored due to requestId mismatch');
          return;
        }
        updateRequestIdRef.current = null;
        clearSaveTimeout();
        setAvatarRemovedLocally(false);
        setSaving(false);
        setError(translateUpdateError(payload.code ?? payload.message, t));
        return;
      }
      if (type === 'players_snapshot') {
        const payload = message as { players?: PlayerProfile[]; Players?: PlayerProfile[] };
        const list = payload.players ?? payload.Players ?? [];
        const updated = list.find((p) => p.id === player?.id);
        if (updated) {
          setPlayer((prev) =>
            avatarRemovedLocally
              ? { ...(prev ?? updated), ...updated, imageKey: null, imageThumbKey: null }
              : { ...(prev ?? updated), ...updated }
          );
        }
      }
    });
    return () => {
      unsubscribe();
    };
  }, [avatarRemovedLocally, clearSaveTimeout, email, golfId, homeClubs, normalizedHomeClubs, player?.id, searchVisibility, t, user?.token]);

  const sendProfileUpdate = useCallback(
    (
      nextName: string,
      nextEmail: string,
      nextGender: string,
      nextHandicap: number | null,
      nextHandicapDate: string | null,
      nextHomeClubs: HomeClubProfile[],
      nextGolfId: string,
      nextSearchVisibility: SearchVisibility
    ) => {
      if (!player?.id) return;
      setSaving(true);
      setError(null);
      setStatusNote(t('profile.saving'));
      const requestId = generateId();
      updateRequestIdRef.current = requestId;
      socketRef.current.setAuthToken(user?.token ?? null);
      const payload = {
        type: 'player.profile.update',
        playerId: player.id,
        requestId,
        displayName: nextName.trim(),
        email: nextEmail,
        gender: nextGender,
        handicap: nextHandicap,
        handicapDate: nextHandicapDate,
        golfId: nextGolfId,
        searchVisibility: nextSearchVisibility,
        homeClubs: nextHomeClubs.map((club) => ({
          clubId: club.clubId,
          clubName: club.clubName,
          clubCity: club.clubCity ?? null,
          isPrimary: club.isPrimary === true,
        })),
      };
      console.info('[profile] sending player.profile.update', {
        requestId,
        playerId: player.id,
        hasEmail: payload.email !== undefined,
        hasHomeClubs: Array.isArray(payload.homeClubs),
        homeClubCount: payload.homeClubs.length,
      });
      const sent = socketRef.current.send(payload);
      if (sent !== 'sent') {
        console.info('[profile] player.profile.update queued until the socket reopens', {
          requestId,
          playerId: player.id,
        });
      }
      armSaveTimeout(requestId);
    },
    [armSaveTimeout, player?.id, t, user?.token]
  );

  // Sent on its own rather than through the profile save: every other field on this screen is
  // null in this payload, and the handler skips nulls, so nothing else is touched.
  const handleEnhancedInputToggle = useCallback(
    (next: boolean) => {
      if (!player?.id) return;
      setEnhancedInput(next);
      setEnhancedInputSaving(true);
      setError(null);
      socketRef.current.setAuthToken(user?.token ?? null);
      const sent = socketRef.current.send({
        type: 'player.profile.update',
        playerId: player.id,
        requestId: generateId(),
        enhancedInput: next,
      });
      setEnhancedInputSaving(false);
      if (sent === 'dropped') {
        setEnhancedInput(!next);
        setError(t('profile.saveFailed'));
      }
    },
    [player?.id, t, user?.token]
  );

  // Applied locally straight away so every open screen reflows immediately, then persisted.
  // On failure the local value is put back, because a setting that silently disagrees with
  // the server is worse than one that visibly refused.
  const handleDistanceUnitChange = useCallback(
    (next: DistanceUnit) => {
      if (!player?.id || next === distanceUnit) return;
      const previous = distanceUnit;
      setDistanceUnit(next);
      setDistanceUnitSaving(true);
      setError(null);
      socketRef.current.setAuthToken(user?.token ?? null);
      const sent = socketRef.current.send({
        type: 'player.profile.update',
        playerId: player.id,
        requestId: generateId(),
        distanceUnit: next,
      });
      setDistanceUnitSaving(false);
      if (sent === 'dropped') {
        setDistanceUnit(previous);
        setError(t('profile.saveFailed'));
      }
    },
    [distanceUnit, player?.id, setDistanceUnit, t, user?.token]
  );

  const handleSaveProfile = useCallback(() => {
    const trimmed = displayName.trim();
    const trimmedEmail = email.trim();
    if (!player?.id) return;
    const normalizedGender = gender.trim();
    const handicapText = handicap.trim();
    const handicapDateText = handicapDate.trim();

    if (handicapText && !handicapDateText) {
      setError(t('profile.handicapDateRequired'));
      return;
    }
    if (handicapDateText && !handicapText) {
      setError(t('profile.handicapRequired'));
      return;
    }

    let parsedHandicap: number | null = null;
    if (handicapText) {
      parsedHandicap = parseDecimalInput(handicapText);
      if (!Number.isFinite(parsedHandicap)) {
        setError(t('profile.invalidHandicap'));
        return;
      }
    }

    let normalizedHandicapDate: string | null = null;
    if (handicapDateText) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(handicapDateText)) {
        setError(t('profile.invalidHandicapDate'));
        return;
      }
      normalizedHandicapDate = handicapDateText;
    }

    const trimmedGolfId = golfId.trim();
    if (!isValidGolfId(trimmedGolfId)) {
      setError(t('profile.invalidGolfId'));
      return;
    }
    if (searchVisibility === 'golfId' && trimmedGolfId.length === 0) {
      setError(t('profile.searchVisibilityGolfIdRequired'));
      return;
    }

    const currentName = player.displayName ?? player.name ?? '';
    const currentGender = (player.gender ?? player.Gender ?? '').trim();
    const currentHandicap = player.handicap ?? player.Handicap ?? null;
    const currentHandicapDateRaw = player.handicapDate ?? player.HandicapDate ?? null;
    const currentHandicapDate = currentHandicapDateRaw ? String(currentHandicapDateRaw).slice(0, 10) : null;
    const currentEmail = initialEmail.trim();
    const sameName = trimmed === currentName;
    const sameEmail = trimmedEmail === currentEmail;
    const sameGender = normalizedGender === currentGender;
    const sameHandicap = (currentHandicap === null ? null : Number(currentHandicap)) === parsedHandicap;
    const sameDate = currentHandicapDate === normalizedHandicapDate;
    const nextHomeClubs = normalizedHomeClubs(homeClubs);
    const sameHomeClubs = JSON.stringify(nextHomeClubs) === JSON.stringify(normalizedHomeClubs(initialHomeClubs));
    const sameGolfId = trimmedGolfId === initialGolfId.trim();
    const sameSearchVisibility = searchVisibility === initialSearchVisibility;
    if (
      sameName &&
      sameEmail &&
      sameGender &&
      sameHandicap &&
      sameDate &&
      sameHomeClubs &&
      sameGolfId &&
      sameSearchVisibility
    ) {
        return;
    }

    sendProfileUpdate(
      trimmed,
      trimmedEmail,
      normalizedGender,
      parsedHandicap,
      normalizedHandicapDate,
      nextHomeClubs,
      trimmedGolfId,
      searchVisibility
    );
  }, [displayName, email, gender, golfId, handicap, handicapDate, homeClubs, initialEmail, initialGolfId, initialHomeClubs, initialSearchVisibility, normalizedHomeClubs, player, searchVisibility, sendProfileUpdate, t]);

  const parseDateInput = (value: string) => localDateToDate(value);
  const formatDateInput = (value: Date) => formatLocalDate(value);

  const handleRemoveAvatar = useCallback(() => {
    if (!player?.id || (!player.imageKey && !player.imageThumbKey)) return;
    setSaving(true);
    setError(null);
    setStatusNote(t('profile.saving'));
    const requestId = generateId();
    updateRequestIdRef.current = requestId;
    socketRef.current.setAuthToken(user?.token ?? null);
    // 'queued' is not a failure — the update is flushed when the socket (re)opens;
    // armSaveTimeout is the only thing that gives up.
    socketRef.current.send({
      type: 'player.profile.update',
      playerId: player.id,
      requestId,
      imageKey: null,
      removeAvatar: true,
    });
    armSaveTimeout(requestId);
    setAvatarRemovedLocally(true);
    setPlayer((prev) => (prev ? { ...prev, imageKey: null, imageThumbKey: null } : prev));
  }, [armSaveTimeout, player?.id, player?.imageKey, player?.imageThumbKey, t, user?.token]);

  const buildAvatarSvg = useCallback((seed: string) => createAvatarSvg(seed), []);

  const generateAvatarChoices = useCallback(() => {
    setAvatarChoices(() =>
      Array.from({ length: 12 }, () => ({
        id: generateId(),
        seed: generateId(),
      }))
    );
  }, []);

  useEffect(() => {
    if (!avatarPickerOpen) return;
    if (avatarRefreshLock.current) return;
    avatarRefreshLock.current = true;
    if (avatarChoices.length === 0) {
      generateAvatarChoices();
    }
    setTimeout(() => {
      avatarRefreshLock.current = false;
    }, 0);
  }, [avatarPickerOpen, generateAvatarChoices, avatarChoices.length]);

  const openAvatarPicker = useCallback(() => {
    generateAvatarChoices();
    setAvatarPickerOpen(true);
  }, [generateAvatarChoices]);

  // This flag only hides the tip for the current visit — a lasting opt-out is stored
  // per user by the coach mark service, so the tip returns on the next visit.
  useEffect(() => {
    setImageCoachMarkDismissed(false);
  }, [isFocused]);

  useEffect(() => {
    if (pathname === '/profile') {
      setImageCoachMarkDismissed(false);
    }
  }, [pathname]);

  const requestPresign = useCallback(
    (payload: PendingUpload) => {
      if (!player?.id) return;
      pendingUploadRef.current = payload;
      const requestId = generateId();
      imageRequestIdRef.current = requestId;
      setUploading(true);
      setStatusNote(t('profile.uploading'));
      setError(null);
      socketRef.current.setAuthToken(user?.token ?? null);
      socketRef.current.send({
        type: 'player.image.presign',
        playerId: player.id,
        requestId,
        fileName: payload.fileName,
        contentType: payload.contentType,
        fileSize: payload.fileSize,
      });
    },
    [player?.id, t, user?.token]
  );

  const selectAvatar = useCallback(
    (seed: string) => {
      if (!player?.id) return;
      const svg = buildAvatarSvg(seed);
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      requestPresign({
        blob,
        contentType: 'image/svg+xml',
        fileName: `player-${player.id}-${seed}.svg`,
        fileSize: blob.size,
      });
      setAvatarPickerOpen(false);
    },
    [buildAvatarSvg, player?.id, requestPresign]
  );

  const handleWebSelect = useCallback(
    (event: any) => {
      const file = event.target.files?.[0] as File | undefined;
      if (!file || !player?.id) return;
      requestPresign({
        blob: file,
        contentType: file.type || 'image/jpeg',
        fileName: file.name,
        fileSize: file.size,
      });
      event.target.value = '';
    },
    [player?.id, requestPresign]
  );

  const handleMobileSelect = useCallback(async () => {
    if (!player?.id) return;
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      setError(t('profile.permissionDenied'));
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.9,
    });
    if (result.canceled || result.assets.length === 0) return;
    const asset = result.assets[0];
    const response = await fetch(asset.uri);
    const blob = await response.blob();
    requestPresign({
      blob,
      contentType: blob.type || 'image/jpeg',
      fileName: asset.fileName ?? `player-${player.id}.jpg`,
      fileSize: blob.size,
    });
  }, [player?.id, requestPresign, t]);

  const uploadButtonPress = useCallback(() => {
    if (uploading) return;
    if (Platform.OS === 'web') {
      fileInputRef.current?.click();
    } else {
      void handleMobileSelect();
    }
  }, [handleMobileSelect, uploading]);

  const goBack = useCallback(() => {
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/my-tours');
  }, [router]);

  const removeHomeClub = useCallback((clubId: string) => {
    setHomeClubs((prev) => {
      const remaining = prev.filter((club) => club.clubId !== clubId);
      if (remaining.length > 0 && !remaining.some((club) => club.isPrimary)) {
        return remaining.map((club, index) => ({ ...club, isPrimary: index === 0 }));
      }
      return remaining;
    });
  }, []);

  const setPrimaryHomeClub = useCallback((clubId: string) => {
    setHomeClubs((prev) => prev.map((club) => ({ ...club, isPrimary: club.clubId === clubId })));
  }, []);

  const addHomeClub = useCallback((club: ClubOption) => {
    setHomeClubs((prev) => {
      if (prev.some((existing) => existing.clubId === club.id)) {
        return prev;
      }
      const isPrimary = prev.length === 0;
      return [
        ...prev,
        {
          clubId: club.id,
          clubName: club.name,
          clubCity: club.city ?? null,
          isPrimary,
        },
      ];
    });
    setClubSearch('');
    setClubPickerOpen(false);
  }, []);

  return (
    <ScrollView style={styles.screen} contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      <Pressable style={styles.backButton} onPress={goBack}>
        <Text style={styles.backButtonText}>{t('menu.back')}</Text>
      </Pressable>
      <View style={styles.header}>
        <View style={styles.avatarWrap}>
          {resolvedImage ? (
            <Image source={{ uri: resolvedImage }} style={styles.avatar} resizeMode="cover" />
          ) : (
            <View style={styles.avatarPlaceholder} />
          )}
        </View>
        <View style={styles.headerContent}>
          <Text style={styles.title}>{t('profile.title')}</Text>
          {loading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color="#A1D493" />
              <Text style={styles.meta}>{t('profile.loading')}</Text>
            </View>
          ) : null}
          {statusNote ? <Text style={styles.meta}>{statusNote}</Text> : null}
          {error ? <Text style={styles.error}>{error}</Text> : null}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.displayNameLabel')}</Text>
        <GoffyInput
          value={displayName}
          onChangeText={setDisplayName}
          editable={!loading && !saving}
          placeholder={t('profile.displayNamePlaceholder')}
        />
        <Text style={styles.label}>{t('profile.fullNameLabel')}</Text>
        <GoffyInput
          value={fullName}
          editable={false}
          placeholder={t('profile.fullNamePlaceholder')}
          style={styles.readonlyInput}
        />
        <Text style={styles.label}>{t('profile.emailLabel')}</Text>
        <GoffyInput
          value={email}
          onChangeText={setEmail}
          editable={!loading && !saving}
          placeholder={t('profile.emailPlaceholder')}
          keyboardType="email-address"
          autoCapitalize="none"
        />
        <Text style={styles.label}>{t('profile.genderLabel')}</Text>
        <View style={styles.genderOptionsRow}>
          {genderOptions.map((option) => {
            const selected = gender === option.value;
            return (
              <Pressable
                key={option.value}
                style={[styles.genderOptionButton, selected && styles.genderOptionButtonSelected]}
                onPress={() => setGender(option.value)}
                disabled={loading || saving}
              >
                <Text style={[styles.genderOptionText, selected && styles.genderOptionTextSelected]}>
                  {option.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
        <View style={styles.inlineRow}>
          <View style={styles.inlineInputGroup}>
            <Text style={styles.label}>{t('profile.handicapLabel')}</Text>
            <GoffyInput
              value={handicap}
              onChangeText={setHandicap}
              editable={!loading && !saving}
              placeholder={t('profile.handicapPlaceholder')}
              keyboardType="decimal-pad"
            />
          </View>
          <View style={styles.inlineInputGroup}>
            <Text style={styles.label}>{t('profile.handicapDateLabel')}</Text>
            <Pressable
              style={styles.input}
              onPress={() => setHandicapDatePickerOpen(true)}
              disabled={loading || saving}
            >
              <Text style={handicapDate ? styles.inputText : styles.inputPlaceholderText}>
                {handicapDate || t('profile.handicapDatePlaceholder')}
              </Text>
            </Pressable>
          </View>
        </View>
        <Text style={styles.label}>{t('profile.golfIdLabel')}</Text>
        <GoffyInput
          value={golfId}
          onChangeText={setGolfId}
          editable={!loading && !saving}
          placeholder={t('profile.golfIdPlaceholder')}
          autoCapitalize="characters"
          autoCorrect={false}
          maxLength={golfIdMaxLength}
        />
        <Text style={styles.meta}>{t('profile.golfIdHelp')}</Text>
        <Text style={styles.label}>{t('profile.searchVisibilityLabel')}</Text>
        <View style={styles.genderOptionsRow}>
          {searchVisibilityOptions.map((option) => {
            const selected = searchVisibility === option;
            return (
              <Pressable
                key={option}
                testID={`search-visibility-${option}`}
                style={[styles.genderOptionButton, selected && styles.genderOptionButtonSelected]}
                onPress={() => setSearchVisibility(option)}
                disabled={loading || saving}
              >
                <Text style={[styles.genderOptionText, selected && styles.genderOptionTextSelected]}>
                  {t(searchVisibilityLabelKeys[option])}
                </Text>
              </Pressable>
            );
          })}
        </View>
        <Text style={styles.meta}>{t(searchVisibilityHelpKeys[searchVisibility])}</Text>
        <Text style={styles.label}>{t('profile.homeClubsLabel')}</Text>
        {homeClubs.length === 0 ? (
          <Text style={styles.meta}>{t('profile.homeClubsEmpty')}</Text>
        ) : (
          <View style={styles.homeClubList}>
            {homeClubs.map((club) => (
              <View key={club.clubId} style={styles.homeClubItem}>
                <View style={styles.homeClubTextWrap}>
                  <Text style={styles.homeClubName}>{club.clubName}</Text>
                  {club.clubCity ? <Text style={styles.homeClubMeta}>{club.clubCity}</Text> : null}
                </View>
                <View style={styles.homeClubActions}>
                  <Pressable
                    style={[styles.homeClubActionButton, club.isPrimary && styles.homeClubActionButtonPrimary]}
                    onPress={() => setPrimaryHomeClub(club.clubId)}
                    disabled={loading || saving}
                  >
                    <Text style={[styles.homeClubActionButtonText, club.isPrimary && styles.homeClubActionButtonTextPrimary]}>
                      {club.isPrimary ? t('profile.homeClubsPrimary') : t('profile.homeClubsSetPrimary')}
                    </Text>
                  </Pressable>
                  <Pressable
                    style={styles.homeClubRemoveButton}
                    onPress={() => removeHomeClub(club.clubId)}
                    disabled={loading || saving}
                  >
                    <Text style={styles.homeClubRemoveText}>{t('profile.homeClubsRemove')}</Text>
                  </Pressable>
                </View>
              </View>
            ))}
          </View>
        )}
        <Pressable
          style={[styles.addHomeClubButton, (loading || saving) && styles.primaryButtonDisabled]}
          onPress={() => {
            setClubSearch('');
            setClubPickerOpen(true);
          }}
          disabled={loading || saving}
        >
          <Text style={styles.addHomeClubButtonText}>{t('profile.homeClubsAdd')}</Text>
        </Pressable>
        <GoffyButton
          label={t('profile.save')}
          onPress={handleSaveProfile}
          disabled={saving}
          tone="accent"
          oldButtonStyle={styles.primaryButton}
          oldTextStyle={styles.primaryButtonText}
          oldDisabledStyle={styles.primaryButtonDisabled}
          style={styles.primaryButtonImageMode}
          textStyle={styles.primaryButtonImageText}
        />
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.imageLabel')}</Text>
        <View style={styles.buttonRow} ref={imageActionsAnchorRef}>
          <GoffyButton
            label={t('profile.uploadImage')}
            onPress={uploadButtonPress}
            disabled={uploading}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            oldDisabledStyle={styles.primaryButtonDisabled}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
          <GoffyButton
            label={t('profile.selectAvatar')}
            onPress={openAvatarPicker}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
          <GoffyButton
            label={t('profile.removeAvatar')}
            onPress={handleRemoveAvatar}
            disabled={saving || uploading || !resolvedImage}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            oldDisabledStyle={styles.primaryButtonDisabled}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.enhancedInputSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.enhancedInputHint')}</Text>
        <View style={styles.coachTipsRow}>
          <Text style={styles.coachTipsLabel}>{t('profile.enhancedInputToggleLabel')}</Text>
          <Switch
            testID="enhanced-input-toggle"
            value={enhancedInput}
            disabled={loading || enhancedInputSaving || !player?.id}
            onValueChange={handleEnhancedInputToggle}
            accessibilityLabel={t('profile.enhancedInputToggleLabel')}
            trackColor={{ false: '#3f5a45', true: '#A1D493' }}
          />
          <Text style={styles.meta}>
            {enhancedInput ? t('profile.enhancedInputOn') : t('profile.enhancedInputOff')}
          </Text>
        </View>
      </View>

      {/* Presentation only — every distance stays stored in metres, so switching this
          changes what is displayed and nothing that is saved. */}
      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.distanceUnitSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.distanceUnitHint')}</Text>
        <View style={styles.unitRow}>
          {(['metric', 'imperial'] as const).map((option) => (
            <Pressable
              key={option}
              testID={`distance-unit-${option}`}
              accessibilityRole="radio"
              aria-checked={distanceUnit === option}
              disabled={loading || distanceUnitSaving || !player?.id}
              style={[styles.unitChip, distanceUnit === option && styles.unitChipActive]}
              onPress={() => handleDistanceUnitChange(option)}
            >
              <Text style={styles.unitChipText}>
                {t(option === 'metric' ? 'profile.distanceUnitMetric' : 'profile.distanceUnitImperial')}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>

      {/* Per-device consent, deliberately NOT a profile flag: switching sensors on must
          never follow an account to a new phone. See stores/shotTrackingState. */}
      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.shotTrackingSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.shotTrackingHint')}</Text>
        <View style={styles.coachTipsRow}>
          <Text style={styles.coachTipsLabel}>{t('profile.shotTrackingToggleLabel')}</Text>
          <Switch
            testID="shot-tracking-toggle"
            value={shotTrackingEnabled}
            onValueChange={setShotTrackingEnabled}
            accessibilityLabel={t('profile.shotTrackingToggleLabel')}
            trackColor={{ false: '#3f5a45', true: '#A1D493' }}
          />
          <Text style={styles.meta}>
            {shotTrackingEnabled ? t('profile.shotTrackingOn') : t('profile.shotTrackingOff')}
          </Text>
        </View>
        {Platform.OS !== 'android' ? (
          <Text style={styles.meta}>{t('profile.shotTrackingAndroidOnly')}</Text>
        ) : null}
        {isDeveloper ? (
          <GoffyButton
            label={t('profile.shotTrackingDebugLink')}
            onPress={() => router.push('/shot-tracking-debug')}
            tone="dark"
            testID="shot-tracking-debug-link"
          />
        ) : null}
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.showDistanceSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.showDistanceHint')}</Text>
        <View style={styles.coachTipsRow}>
          <Text style={styles.coachTipsLabel}>{t('profile.showDistanceToggleLabel')}</Text>
          <Switch
            testID="show-distance-toggle"
            value={showDistanceEnabled}
            onValueChange={setShowDistanceEnabled}
            accessibilityLabel={t('profile.showDistanceToggleLabel')}
            trackColor={{ false: '#3f5a45', true: '#A1D493' }}
          />
          <Text style={styles.meta}>
            {showDistanceEnabled ? t('profile.showDistanceOn') : t('profile.showDistanceOff')}
          </Text>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.coachTipsSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.coachTipsHint')}</Text>
        <View style={styles.coachTipsRow}>
          <Text style={styles.coachTipsLabel}>{t('profile.coachTipsToggleLabel')}</Text>
          <Switch
            testID="coach-tips-toggle"
            value={coachTipsEnabled}
            disabled={coachTipsLoading || coachTipsSaving}
            onValueChange={(next) => {
              setCoachTipsError(null);
              void setCoachTipsEnabled(next).catch(() => {
                setCoachTipsError(t('profile.coachTipsSaveFailed'));
              });
            }}
            accessibilityLabel={t('profile.coachTipsToggleLabel')}
            trackColor={{ false: '#3f5a45', true: '#A1D493' }}
          />
          <Text style={styles.meta}>
            {coachTipsEnabled ? t('profile.coachTipsOn') : t('profile.coachTipsOff')}
          </Text>
        </View>
        {hasDismissedTips || !coachTipsEnabled ? (
          <View style={styles.buttonRow}>
            <GoffyButton
              label={coachTipsSaving ? t('profile.coachTipsResetting') : t('profile.coachTipsReset')}
              onPress={() => {
                setCoachTipsError(null);
                void resetCoachTips().catch(() => {
                  setCoachTipsError(t('profile.coachTipsSaveFailed'));
                });
              }}
              disabled={coachTipsSaving}
              tone="dark"
              testID="coach-tips-reset"
              oldButtonStyle={styles.secondaryButton}
              oldTextStyle={styles.secondaryButtonText}
              oldDisabledStyle={styles.primaryButtonDisabled}
              style={styles.secondaryButtonImageMode}
              textStyle={styles.secondaryButtonImageText}
            />
          </View>
        ) : null}
        {coachTipsError ? <Text style={styles.error}>{coachTipsError}</Text> : null}
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>{t('profile.passwordSectionLabel')}</Text>
        {hasPassword === false ? <Text style={styles.meta}>{t('profile.passwordSetHint')}</Text> : null}
        <View style={styles.buttonRow}>
          <GoffyButton
            label={hasPassword === false ? t('profile.setPassword') : t('profile.changePassword')}
            onPress={() => {
              resetPasswordState();
              setPasswordModalOpen(true);
            }}
            disabled={loading}
            tone="dark"
            oldButtonStyle={styles.secondaryButton}
            oldTextStyle={styles.secondaryButtonText}
            oldDisabledStyle={styles.primaryButtonDisabled}
            style={styles.secondaryButtonImageMode}
            textStyle={styles.secondaryButtonImageText}
          />
        </View>
      </View>

      <View style={[styles.section, styles.dangerSection]}>
        <Text style={styles.label}>{t('profile.deleteAccountSectionLabel')}</Text>
        <Text style={styles.meta}>{t('profile.deleteAccountHint')}</Text>
        <View style={styles.buttonRow}>
          <Pressable
            style={[styles.dangerButton, (loading || deletingAccount) && styles.primaryButtonDisabled]}
            onPress={() => {
              resetDeleteAccountState();
              setDeleteAccountModalOpen(true);
            }}
            disabled={loading || deletingAccount}
            testID="delete-account-open"
            accessibilityLabel={t('profile.deleteAccount')}
          >
            <Text style={styles.dangerButtonText}>{t('profile.deleteAccount')}</Text>
          </Pressable>
        </View>
      </View>

      {Platform.OS === 'web' ? (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleWebSelect}
          style={{ display: 'none' }}
        />
      ) : null}
      <Modal transparent visible={clubPickerOpen} animationType="fade" onRequestClose={() => setClubPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setClubPickerOpen(false)}>
          <View style={styles.avatarModalCard}>
            <Text style={styles.avatarModalTitle}>{t('profile.homeClubsPickerTitle')}</Text>
            <GoffyInput
              value={clubSearch}
              onChangeText={setClubSearch}
              editable={!loadingClubOptions}
              placeholder={t('profile.homeClubsSearchPlaceholder')}
            />
            <ScrollView style={styles.homeClubPickerList} contentContainerStyle={styles.homeClubPickerListContent}>
              {loadingClubOptions ? <Text style={styles.avatarLoading}>{t('profile.homeClubsLoading')}</Text> : null}
              {!loadingClubOptions && filteredClubOptions.length === 0 ? (
                <Text style={styles.avatarLoading}>{t('profile.homeClubsNoResults')}</Text>
              ) : null}
              {!loadingClubOptions
                ? filteredClubOptions.map((club) => (
                    <Pressable key={club.id} style={styles.clubOptionRow} onPress={() => addHomeClub(club)}>
                      <Text style={styles.clubOptionName}>{club.name}</Text>
                      <Text style={styles.clubOptionMeta}>
                        {[club.city, club.country].filter(Boolean).join(', ') || t('profile.homeClubsUnknownLocation')}
                      </Text>
                    </Pressable>
                  ))
                : null}
            </ScrollView>
            <View style={styles.avatarModalActions}>
              <Pressable
                style={[styles.moreButton, loadingClubOptions && styles.primaryButtonDisabled]}
                onPress={() => {
                  void loadClubOptions();
                }}
                disabled={loadingClubOptions}
              >
                <Text style={styles.moreButtonText}>{t('profile.homeClubsRefresh')}</Text>
              </Pressable>
              <Pressable style={styles.closeButton} onPress={() => setClubPickerOpen(false)}>
                <Text style={styles.closeButtonText}>{t('profile.close')}</Text>
              </Pressable>
            </View>
          </View>
        </Pressable>
      </Modal>
      <ChangePasswordModal
        visible={passwordModalOpen}
        requireCurrent={hasPassword !== false}
        title={hasPassword === false ? t('profile.setPassword') : t('profile.changePassword')}
        pending={passwordSaving}
        errorText={passwordError}
        onSubmit={changePassword}
        onClose={() => setPasswordModalOpen(false)}
      />
      <DeleteAccountModal
        visible={deleteAccountModalOpen}
        pending={deletingAccount}
        errorText={deleteAccountError}
        onConfirm={() => {
          void deleteAccount();
        }}
        onClose={() => {
          if (deletingAccount) return;
          setDeleteAccountModalOpen(false);
        }}
      />
      <Modal transparent visible={avatarPickerOpen} animationType="fade" onRequestClose={() => setAvatarPickerOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setAvatarPickerOpen(false)}>
          <View style={styles.avatarModalCard}>
            <Text style={styles.avatarModalTitle}>{t('profile.avatarPickerTitle')}</Text>
            <View style={styles.avatarGrid}>
              {avatarChoices.length === 0 ? (
                <Text style={styles.avatarLoading}>{t('profile.avatarLoading')}</Text>
              ) : (
                avatarChoices.map(({ id, seed }) => {
                  const svg = buildAvatarSvg(seed);
                  return (
                    <Pressable key={id} testID="avatar-option" style={styles.avatarChoice} onPress={() => selectAvatar(seed)}>
                      <SvgXml xml={svg} width="100%" height="100%" />
                    </Pressable>
                  );
                })
              )}
            </View>
            <View style={styles.avatarModalActions}>
              <Pressable style={styles.moreButton} onPress={generateAvatarChoices}>
                <Text style={styles.moreButtonText}>{t('profile.avatarMore')}</Text>
              </Pressable>
              <Pressable style={styles.closeButton} onPress={() => setAvatarPickerOpen(false)}>
                <Text style={styles.closeButtonText}>{t('profile.close')}</Text>
              </Pressable>
            </View>
          </View>
        </Pressable>
      </Modal>
      <PlatformDatePicker
        open={handicapDatePickerOpen}
        date={parseDateInput(handicapDate) ?? new Date()}
        onConfirm={(date) => {
          setHandicapDate(formatDateInput(date));
          setHandicapDatePickerOpen(false);
        }}
        onCancel={() => setHandicapDatePickerOpen(false)}
      />
      <CoachMarkCallout
        visible={showImageCoachMark}
        targetRef={imageActionsAnchorRef}
        title={t('coachMarks.profileImageActionsTitle')}
        description={t('coachMarks.profileImageActionsBody')}
        actionLabel={t('coachMarks.gotIt')}
        onDismiss={() => setImageCoachMarkDismissed(true)}
        hideForeverLabel={t('coachMarks.hideForever')}
        disableAllLabel={t('coachMarks.disableAll')}
        onDismissForever={() => {
          setImageCoachMarkDismissed(true);
          void dismissImageCoachMarkForever().catch(() => {
            setImageCoachMarkDismissed(false);
          });
        }}
        onDisableAll={() => {
          setImageCoachMarkDismissed(true);
          void disableAllCoachMarks().catch(() => {
            setImageCoachMarkDismissed(false);
          });
        }}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#07200D',
  },
  container: {
    flexGrow: 1,
    padding: 16,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#0C2510',
  },
  backButton: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#C8C8B0',
    paddingHorizontal: 10,
    height: 34,
    justifyContent: 'center',
  },
  backButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 13,
  },
  avatarWrap: {
    width: 88,
    height: 88,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#1A3A20',
  },
  avatar: {
    width: '100%',
    height: '100%',
  },
  avatarPlaceholder: {
    flex: 1,
    backgroundColor: '#C8C8B0',
  },
  headerContent: {
    flex: 1,
    gap: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  meta: {
    color: '#A1D493',
    fontSize: 12,
  },
  error: {
    color: '#fca5a5',
    fontSize: 12,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  unitRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  unitChip: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(226,232,240,0.35)',
  },
  unitChipActive: {
    borderColor: '#A1D493',
    borderWidth: 2,
  },
  unitChipText: {
    color: appColors.text.primary,
    fontSize: 14,
  },
  coachTipsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  coachTipsLabel: {
    color: appColors.text.primary,
    fontSize: 14,
    flexShrink: 1,
  },
  section: {
    gap: 10,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  dangerSection: {
    borderColor: '#7f1d1d',
  },
  dangerButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#7f1d1d',
    backgroundColor: '#2b0b0b',
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  dangerButtonText: {
    color: '#fecaca',
    fontWeight: '700',
    fontSize: 14,
  },
  inlineRow: {
    flexDirection: 'row',
    gap: 10,
  },
  inlineInputGroup: {
    flex: 1,
    gap: 6,
  },
  genderOptionsRow: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  genderOptionButton: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  genderOptionButtonSelected: {
    borderColor: '#A1D493',
    backgroundColor: '#1A3A20',
  },
  genderOptionText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  genderOptionTextSelected: {
    color: appColors.text.primary,
  },
  label: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  // Only the handicap-date field still uses this: it is a Pressable dressed as a field, so it
  // cannot be a GoffyInput. Kept in step with GoffyInput's frame so it matches the real inputs
  // beside it — the old '#07200D' fill was identical to the section behind it.
  input: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: appColors.text.primary,
    fontSize: 14,
    backgroundColor: appColors.surface.input,
  },
  readonlyInput: {
    opacity: 0.8,
  },
  inputText: {
    color: appColors.text.primary,
    fontSize: 14,
  },
  // Matches GoffyInput's placeholder tone, so the date field reads like the fields around it.
  inputPlaceholderText: {
    color: appColors.text.muted,
    fontSize: 14,
  },
  primaryButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#A1D493',
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonImageMode: {
    width: 190,
    height: 56,
  },
  primaryButtonImageText: {
    fontSize: 16,
  },
  primaryButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  homeClubList: {
    gap: 8,
  },
  homeClubItem: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1e4d22',
    backgroundColor: '#1b351a',
    padding: 10,
    gap: 8,
  },
  homeClubTextWrap: {
    gap: 2,
  },
  homeClubName: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  homeClubMeta: {
    color: '#C8C8B0',
    fontSize: 12,
  },
  homeClubActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  homeClubActionButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.border.default,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#07200D',
  },
  // Marks the club that IS primary, so it has to look distinct. The fill was
  // appColors.border.default — a dark green at 15% opacity over '#07200D' — which composited to
  // within a couple of shades of the unselected state, making the two indistinguishable.
  homeClubActionButtonPrimary: {
    backgroundColor: appColors.accent.primary,
    borderColor: appColors.accent.primary,
  },
  homeClubActionButtonText: {
    color: '#C8C8B0',
    fontWeight: '700',
    fontSize: 12,
  },
  homeClubActionButtonTextPrimary: {
    color: appColors.text.inverse,
  },
  homeClubRemoveButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7f1d1d',
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#2b0b0b',
  },
  homeClubRemoveText: {
    color: '#fecaca',
    fontWeight: '700',
    fontSize: 12,
  },
  addHomeClubButton: {
    alignSelf: 'flex-start',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1e4d22',
    backgroundColor: '#1b351a',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  addHomeClubButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 14,
  },
  secondaryButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  secondaryButtonText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  secondaryButtonImageMode: {
    width: 190,
    height: 56,
  },
  secondaryButtonImageText: {
    fontSize: 16,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.18)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  avatarModalCard: {
    width: '100%',
    maxWidth: 520,
    backgroundColor: '#07200D',
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
  },
  avatarModalTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: appColors.text.primary,
    marginBottom: 12,
  },
  avatarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  homeClubPickerList: {
    maxHeight: 320,
    marginTop: 10,
  },
  homeClubPickerListContent: {
    gap: 8,
    paddingBottom: 8,
  },
  clubOptionRow: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1e4d22',
    backgroundColor: '#1b351a',
    paddingHorizontal: 10,
    paddingVertical: 10,
    gap: 2,
  },
  clubOptionName: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 14,
  },
  clubOptionMeta: {
    color: '#C8C8B0',
    fontSize: 12,
  },
  avatarLoading: {
    color: appColors.text.primary,
    fontSize: 12,
  },
  avatarTabs: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  avatarTab: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  avatarTabActive: {
    backgroundColor: '#A1D493',
    borderColor: '#A1D493',
  },
  avatarTabText: {
    color: appColors.text.primary,
    fontWeight: '600',
    fontSize: 12,
  },
  avatarChoice: {
    width: 64,
    height: 64,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#1A3A20',
    overflow: 'hidden',
    backgroundColor: '#0C2510',
  },
  avatarChoiceImage: {
    width: '100%',
    height: '100%',
  },
  avatarChoiceSvg: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarModalActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  moreButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
  },
  moreButtonText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  closeButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#A1D493',
  },
  closeButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
});

/** Maps the server's error codes onto translated copy, falling back to a generic save failure. */
function translateUpdateError(code: string | undefined, t: (key: string) => string): string {
  switch (code) {
    case 'user.golfId.invalid':
      return t('profile.invalidGolfId');
    case 'user.golfId.exists':
      return t('profile.golfIdTaken');
    case 'user.searchVisibility.golfIdRequired':
      return t('profile.searchVisibilityGolfIdRequired');
    default:
      return t('profile.saveFailed');
  }
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as any).randomUUID();
  }
  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}
